"use client";

import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode} from "react";
import type {ToolApprovalStatus} from "@repo/ui/gailgpt/tool-approval-status";
import {isPageReviewCapability, sameProposalTarget, type MutationProposal, type ProposalTarget} from "@/services/wingman/mutation-proposal";
import {notifyMutationProposalApplied} from "@/utils/chat/mutationProposalApplied";

/** Every page-reviewable proposal's latest status, whether or not its review is open. */
export type MutationProposalOutcomes = {
   /** Records a status; an outcome showing the target may have changed refreshes the editors and lists showing it. */
   report: (approvalId: string, proposal: MutationProposal, status: ToolApprovalStatus) => void;
   /** An approval of a change to the target is in flight or executing, so saving now could overwrite it. */
   isExecuting: (target: ProposalTarget) => boolean;
};

const MutationProposalOutcomesContext = createContext<MutationProposalOutcomes | null>(null);

export const useMutationProposalOutcomes = () => useContext(MutationProposalOutcomesContext);

const UNSETTLED: ReadonlySet<ToolApprovalStatus> = new Set(["required", "submitting", "approved"]);
/**
 * An uncertain outcome may have saved the change too, so it refreshes like a verified one. A stale
 * one saved nothing, but proves the target changed after Gail's snapshot, so it refreshes as well.
 */
const TARGET_MAY_HAVE_CHANGED: ReadonlySet<ToolApprovalStatus> = new Set(["completed", "indeterminate", "stale"]);
/** A decision in flight may be an approval, so it holds saves until the server reports back. */
const MAY_EXECUTE: ReadonlySet<ToolApprovalStatus> = new Set(["submitting", "approved"]);
/** setTimeout fires at once for longer delays. */
const MAX_TIMER_MS = 2 ** 31 - 1;

const expiry = (proposal: MutationProposal) => Date.parse(proposal.expiresAt);

function removeExpiredExecutions(current: ReadonlyMap<string, MutationProposal>): ReadonlyMap<string, MutationProposal> {
   const live = new Map([...current].filter(([, proposal]) => expiry(proposal) > Date.now()));
   return live.size === current.size ? current : live;
}

/**
 * Proposal outcomes for every conversation on the page, kept above the conversations so that
 * closing a review or remounting a conversation never loses an execution still in flight.
 */
export function MutationProposalOutcomesProvider({children}: {children: ReactNode}) {
   const statuses = useRef(new Map<string, ToolApprovalStatus>());
   const [executing, setExecuting] = useState<ReadonlyMap<string, MutationProposal>>(new Map());

   const report = useCallback((approvalId: string, proposal: MutationProposal, status: ToolApprovalStatus) => {
      const previous = statuses.current.get(approvalId);
      if (previous === status) return;
      statuses.current.set(approvalId, status);
      // Only an outcome that settles while on screen is news; reopening a conversation on old outcomes refreshes nothing.
      if (previous && UNSETTLED.has(previous) && TARGET_MAY_HAVE_CHANGED.has(status) && isPageReviewCapability(proposal.capability)) {
         notifyMutationProposalApplied({capability: proposal.capability, targetId: proposal.targetId});
      }
      const running = MAY_EXECUTE.has(status) && expiry(proposal) > Date.now();
      setExecuting(current => {
         if (current.has(approvalId) === running) return current;
         const next = new Map(current);
         if (running) next.set(approvalId, proposal);
         else next.delete(approvalId);
         return next;
      });
   }, []);

   // An execution that never reports back stops holding editors once its proposal expires.
   useEffect(() => {
      if (!executing.size) return;
      const nextExpiry = Math.min(...[...executing.values()].map(expiry));
      const timer = setTimeout(() => setExecuting(removeExpiredExecutions),
         Math.min(Math.max(0, nextExpiry - Date.now()), MAX_TIMER_MS));
      return () => clearTimeout(timer);
   }, [executing]);

   const isExecuting = useCallback((target: ProposalTarget) =>
      [...executing.values()].some(proposal => sameProposalTarget(proposal, target)), [executing]);

   const value = useMemo(() => ({report, isExecuting}), [report, isExecuting]);
   return <MutationProposalOutcomesContext.Provider value={value}>{children}</MutationProposalOutcomesContext.Provider>;
}
